import React from 'react';

/**
 * V5 报告页内联 SVG 图标 —— lucide 风格简化版。
 *
 * 为什么不直接用 lucide-react：项目尚未引入该依赖，且后端 icon_hint 词表
 * 也未最终定（见 docs/archive/v5/AI-HANDOFF.md / docs/archive/v5/M5-integration-contract.md 「Open Questions」）。
 * 当前先用内联 SVG 覆盖 example JSON 出现的 icon_hint，未列出的名字降级为通用占位图。
 *
 * 后端定下 icon_hint 词表后，再决定是否引 lucide-react 替换本文件。
 */

const COMMON = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const ICON_PATHS = {
  folder: (
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4l10-10-4-4L4 16v4z" />
      <path d="M14 6l4 4" />
    </>
  ),
  'book-open': (
    <>
      <path d="M3 5h7c1.7 0 3 1.3 3 3v12c0-1.7-1.3-3-3-3H3V5z" />
      <path d="M21 5h-7c-1.7 0-3 1.3-3 3v12c0-1.7 1.3-3 3-3h7V5z" />
    </>
  ),
  table: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 16h18M10 4v16M16 4v16" />
    </>
  ),
  megaphone: (
    <>
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </>
  ),
  'user-check': (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <polyline points="16 11 18 13 22 9" />
    </>
  ),
  'bar-chart': (
    <>
      <path d="M4 20V10M10 20V4M16 20v-6M22 20H2" />
    </>
  ),
  'bar-chart-3': (
    <>
      <path d="M4 20V10M10 20V4M16 20v-6M22 20H2" />
    </>
  ),
  scale: (
    <>
      <path d="M12 3v18" />
      <path d="M7 21h10" />
      <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" />
      <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z" />
      <path d="M3 7h4c2 0 5-1 7-2 2 1 5 2 7 2" />
    </>
  ),
  shield: (
    <path d="M12 3l8 3v6c0 4.6-3.4 8.4-8 9-4.6-.6-8-4.4-8-9V6l8-3z" />
  ),
  'shield-check': (
    <>
      <path d="M12 3l8 3v6c0 4.6-3.4 8.4-8 9-4.6-.6-8-4.4-8-9V6l8-3z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  lock: (
    <>
      <rect x="4.5" y="11" width="15" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 1 1 8 0v3" />
    </>
  ),
  heart: (
    <path d="M12 20s-7-4.6-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.4-7 10-7 10z" />
  ),
  brain: (
    <>
      <path d="M9 4c-2.2 0-4 1.6-4 3.7 0 1 .3 1.8.9 2.5-1.1.8-1.9 2.1-1.9 3.5 0 2.4 2 4.3 4.5 4.3.6 0 1.1-.1 1.5-.3" />
      <path d="M15 4c2.2 0 4 1.6 4 3.7 0 1-.3 1.8-.9 2.5 1.1.8 1.9 2.1 1.9 3.5 0 2.4-2 4.3-4.5 4.3-.6 0-1.1-.1-1.5-.3" />
      <path d="M12 4v16" />
    </>
  ),
  'graduation-cap': (
    <>
      <path d="M2 9l10-4 10 4-10 4L2 9z" />
      <path d="M6 11v4c0 1.7 2.7 3 6 3s6-1.3 6-3v-4" />
    </>
  ),
  award: (
    <>
      <circle cx="12" cy="9" r="5" />
      <path d="M8 13l-2 8 6-3 6 3-2-8" />
    </>
  ),
  'thumbs-up': (
    <path d="M7 21V10l5-7c1.5 0 2.5 1.2 2.5 2.5V9h4.5c1.4 0 2.4 1.3 2 2.6l-2 7c-.3 1.1-1.2 2.4-2.5 2.4H7z" />
  ),
  'trending-up': (
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M17 7h4v4" />
    </>
  ),
  quote: (
    <>
      <path d="M6 17V11c0-1.5 1-2.5 2.5-2.5H10V11H8v6H6z" />
      <path d="M14 17V11c0-1.5 1-2.5 2.5-2.5H18V11h-2v6h-2z" />
    </>
  ),
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  clipboard: (
    <>
      <rect x="6" y="4" width="12" height="16" rx="2" />
      <rect x="9" y="3" width="6" height="3" rx="1" />
    </>
  ),
  puzzle: (
    <path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.61a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.23 8.77c.24-.24.581-.353.917-.303.515.077.877.528 1.073 1.01a2.5 2.5 0 1 0 3.259-3.259c-.482-.196-.933-.558-1.01-1.073-.05-.336.062-.676.303-.917l1.525-1.525A2.402 2.402 0 0 1 12 1.998c.617 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02z" />
  ),
  lightbulb: (
    <>
      <path d="M9 18h6" />
      <path d="M10 21h4" />
      <path d="M12 3a6 6 0 0 0-4 10.4c.8.7 1.4 1.5 1.7 2.6h4.6c.3-1.1.9-1.9 1.7-2.6A6 6 0 0 0 12 3z" />
    </>
  ),
  'file-text': (
    <>
      <path d="M7 3h8l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h7M9 17h5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.2" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  'alert-triangle': (
    <>
      <path d="M10.5 4l-7.8 14a1.5 1.5 0 0 0 1.3 2.2h15.6a1.5 1.5 0 0 0 1.3-2.2L13.1 4a1.5 1.5 0 0 0-2.6 0z" />
      <path d="M12 10v4M12 17v.5" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v12" />
      <path d="M7 11l5 5 5-5" />
      <path d="M4 20h16" />
    </>
  ),
  'arrow-left': (
    <>
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </>
  ),
  'arrow-up': (
    <>
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </>
  ),
  list: (
    <>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <path d="M3 6h.01M3 12h.01M3 18h.01" />
    </>
  ),
  home: (
    <>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  circle: <circle cx="12" cy="12" r="6" />,
};

export default function ReportIcon({ name, size = 18, className = '', ...rest }) {
  const path = ICON_PATHS[name] || ICON_PATHS.circle;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      className={`v5r-icon v5r-icon-${name} ${className}`.trim()}
      {...COMMON}
      {...rest}
    >
      {path}
    </svg>
  );
}
